/**
 * LogReceiptDrawer — real partial-GRN + IQC inspection UI on top of
 *   POST /v1/projects/{pid}/mci/entries/{mci}/receipts
 *   POST /v1/projects/{pid}/mci/entries/{mci}/receipts/{n}/inspect
 *   GET  /v1/projects/{pid}/mci/entries/{mci}/receipts
 *
 * Two flows inside one right-side drawer:
 *   1. Log new receipt   — receiptNumber + grnQty + iqcQty + rejectedQty
 *                          + GRN document (via DocumentUploader)
 *   2. Inspect a receipt — pulls from that receipt's iqcQty bucket and
 *                          splits into Accepted / Rejected with an
 *                          inspection report ref (via DocumentUploader).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, Loader2, PackagePlus, ClipboardCheck, PackageCheck, PackageX,
         FileText, ExternalLink, MapPin, Pencil, Check } from 'lucide-react';
import DocumentUploader from '@/components/procurement/DocumentUploader';

const C = { primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F', slate: 'var(--text-primary)',
             slateMuted: 'var(--text-muted)', border: 'rgba(15,23,42,0.10)',
             surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)', success: '#10B981',
             warn: '#F59E0B', danger: '#EF4444' };

function num(v) { return Number(v || 0); }

export default function LogReceiptDrawer({ projectId, mci, unit, scope,
                                            defaultLocation, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [inspectFor, setInspectFor] = useState(null);
  const [pinnedLoc, setPinnedLoc] = useState(defaultLocation || '');
  const [editingPin, setEditingPin] = useState(false);
  // New-receipt form
  const [rn, setRn] = useState('');
  const [grn, setGrn] = useState('');
  const [acc, setAcc] = useState('');
  const [rej, setRej] = useState('');
  const [rem, setRem] = useState('');
  const [docUrl, setDocUrl] = useState('');
  const [warehouse, setWarehouse] = useState(defaultLocation || '');
  const [shop, setShop] = useState('');
  const [hierarchy, setHierarchy] = useState(null);  // {bins:[{location, racks:[]}], stations:[]}
  const [selectedBin, setSelectedBin] = useState('');
  const [selectedRack, setSelectedRack] = useState('');
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/mtx/warehouse-hierarchy`)
      .then(({ data }) => setHierarchy(data))
      .catch(() => setHierarchy({ bins: [], stations: [] }));
  }, [projectId]);
  // When bin+rack chosen, sync canonical location string
  useEffect(() => {
    if (selectedRack) setWarehouse(selectedRack);
    else if (selectedBin) setWarehouse(selectedBin);
  }, [selectedBin, selectedRack]);
  // (iqc state kept for legacy signature; ignored in favour of derived pending)
  const [iqc, setIqc] = useState('');
  const [insRej, setInsRej] = useState('');
  const [insDoc, setInsDoc] = useState('');
  const [insRem, setInsRem] = useState('');
  const [insAcceptLoc, setInsAcceptLoc] = useState(defaultLocation || '');
  const [insRejectLoc, setInsRejectLoc] = useState('');

  // When drawer opens and defaultLocation arrives late, sync it in
  useEffect(() => {
    if (defaultLocation && !pinnedLoc) setPinnedLoc(defaultLocation);
    if (defaultLocation && !warehouse) setWarehouse(defaultLocation);
    if (defaultLocation && !insAcceptLoc) setInsAcceptLoc(defaultLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation]);

  const savePinnedLoc = async () => {
    try {
      await api.patch(`/v1/projects/${projectId}/mci/entries/${mci}/quick`,
        { defaultReceiptLocation: pinnedLoc.trim() });
      toast.success('Location saved on MCI line');
      setEditingPin(false);
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
      onChanged?.();
    } catch (e) { toast.error('Could not save location'); }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(
        `/v1/projects/${projectId}/mci/entries/${mci}/receipts`);
      setData(d);
    } catch (e) {
      toast.error('Could not load receipts');
    }
    setBusy(false);
  }, [projectId, mci]);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!rn.trim()) return toast.error('Receipt number is required');
    const grnN = num(grn); const accN = num(acc); const rejN = num(rej);
    if (grnN <= 0) return toast.error('GRN qty must be > 0');
    if (accN + rejN > grnN + 0.01)
      return toast.error(`Accepted + Rejected on arrival cannot exceed GRN`);
    // IQC Pending = whatever didn't get inspected on arrival
    const pendingN = Math.max(0, grnN - accN - rejN);
    setBusy(true);
    try {
      await api.post(
        `/v1/projects/${projectId}/mci/entries/${mci}/receipts`,
        { receiptNumber: rn.trim(),
          grnQty: grnN, iqcQty: pendingN, rejectedQty: rejN,
          acceptedQty: accN,
          unit: unit || undefined, documentRef: docUrl || undefined,
          warehouseLocation: warehouse || undefined,
          shopLocation: shop || undefined,
          remarks: rem || undefined });
      toast.success(`Receipt ${rn} logged`);
      setRn(''); setGrn(''); setIqc(''); setAcc(''); setRej(''); setRem(''); setDocUrl(''); setWarehouse(''); setShop('');
      setShowNew(false);
      window.dispatchEvent(new CustomEvent('mci-register-updated', { detail: { source: 'log-receipt' } }));
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add receipt');
    }
    setBusy(false);
  };

  const submitInspect = async () => {
    if (!inspectFor) return;
    const total = num(acc) + num(insRej);
    if (total <= 0) return toast.info('Enter accepted or rejected qty');
    const iqcAvail = num(inspectFor.iqcQty);
    if (total > iqcAvail + 0.01)
      return toast.error(`Only ${iqcAvail} ${unit || ''} in IQC — cannot inspect more`);
    setBusy(true);
    try {
      await api.post(
        `/v1/projects/${projectId}/mci/entries/${mci}/receipts/${encodeURIComponent(inspectFor.receiptNumber)}/inspect`,
        { acceptedQty: num(acc), rejectedQty: num(insRej),
          inspectionReportRef: insDoc || undefined,
          acceptedLocation: insAcceptLoc || pinnedLoc || undefined,
          rejectedLocation: insRejectLoc || undefined,
          remarks: insRem || undefined });
      toast.success(`Inspected ${total} ${unit || ''} on ${inspectFor.receiptNumber}`);
      setAcc(''); setInsRej(''); setInsDoc(''); setInsRem(''); setInsRejectLoc('');
      setInspectFor(null);
      window.dispatchEvent(new CustomEvent('mci-register-updated', { detail: { source: 'log-receipt' } }));
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Inspection failed');
    }
    setBusy(false);
  };

  const t = data?.totals || {};
  const receipts = data?.receipts || [];

  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex justify-end" onClick={onClose}
      data-testid="log-receipt-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg h-full flex flex-col shadow-2xl"
        style={{ background: C.surface }}>
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: C.border, background: C.surfaceAlt }}>
          <div className="flex items-center gap-2">
            <PackagePlus className="w-5 h-5" style={{ color: C.accent }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase font-bold tracking-wide" style={{ color: C.primaryText }}>Log Receipt · {mci}</div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>
                Log each GRN against the PO. Split any qty inspected on arrival if useful.
              </div>
            </div>
            <button onClick={onClose} data-testid="log-receipt-close">
              <X className="w-4 h-4" style={{ color: C.slateMuted }} />
            </button>
          </div>
          {/* PO-quantity progress strip — makes the receipt scope obvious.
              Split into three chips (ordered / received / remaining) plus
              a live progress bar so users always know how much of the PO
              is still open to receive. */}
          {(() => {
            const ordered   = Number(scope || 0);
            const received  = Number(t.grn ?? 0);
            const remaining = Number(t.remaining ?? Math.max(0, ordered - received));
            const pct = ordered > 0 ? Math.min(100, (received / ordered) * 100) : 0;
            return (
              <div className="mt-3" data-testid="po-qty-strip">
                <div className="grid grid-cols-3 gap-2 mb-1.5">
                  <div className="p-1.5 rounded" style={{ background: 'var(--card-bg)', border: `1px solid ${C.border}` }}>
                    <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>PO Qty (ordered)</div>
                    <div className="text-[13px] font-bold tabular-nums" style={{ color: C.primaryText }}>{ordered}<span className="text-[9px] ml-0.5" style={{ color: C.slateMuted }}>{unit}</span></div>
                  </div>
                  <div className="p-1.5 rounded" style={{ background: 'var(--card-bg)', border: `1px solid ${C.border}` }}>
                    <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>GRN so far</div>
                    <div className="text-[13px] font-bold tabular-nums" style={{ color: received > 0 ? C.primaryText : C.slateMuted }}>{received}<span className="text-[9px] ml-0.5" style={{ color: C.slateMuted }}>{unit}</span></div>
                  </div>
                  <div className="p-1.5 rounded" style={{ background: remaining > 0 ? 'var(--tone-red-bg)' : 'var(--tone-green-bg)', border: `1px solid ${remaining > 0 ? '#FCA5A5' : '#86EFAC'}` }}>
                    <div className="text-[8.5px] uppercase font-semibold" style={{ color: remaining > 0 ? 'var(--tone-red-text)' : 'var(--tone-green-text)' }}>Remaining</div>
                    <div className="text-[13px] font-bold tabular-nums" style={{ color: remaining > 0 ? C.danger : C.success }}>{remaining}<span className="text-[9px] ml-0.5" style={{ color: C.slateMuted }}>{unit}</span></div>
                  </div>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${C.primary}, ${C.success})` }} />
                </div>
              </div>
            );
          })()}
          {/* Canonical Delivery / GRN / IQC location pin — shared by all receipts on this MCI */}
          <div className="mt-2 flex items-center gap-2 p-1.5 rounded" style={{ background: 'var(--card-bg-alt)', border: `1px dashed ${C.primary}55` }}
            data-testid="mci-location-pin">
            <MapPin className="w-3.5 h-3.5" style={{ color: C.primaryText }} />
            <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Delivery · GRN · IQC</div>
            {editingPin ? (
              <>
                <input value={pinnedLoc} onChange={(e) => setPinnedLoc(e.target.value)}
                  className="flex-1 px-1.5 py-0.5 rounded border text-[11px]"
                  style={{ borderColor: C.border }}
                  placeholder="e.g. WH-01 · Piping Yard · Bay 3"
                  data-testid="mci-location-pin-input" />
                <button onClick={savePinnedLoc} data-testid="mci-location-pin-save"
                  className="p-0.5 rounded" style={{ background: C.success, color: '#fff' }}>
                  <Check className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <div className="flex-1 text-[11px] font-bold" style={{ color: pinnedLoc ? C.primaryText : C.slateMuted }}>
                  {pinnedLoc || 'Not set — click edit to pin a warehouse'}
                </div>
                <button onClick={() => setEditingPin(true)} data-testid="mci-location-pin-edit"
                  className="p-0.5 rounded hover:bg-[var(--hover-bg)]" title="Edit MCI-level default location">
                  <Pencil className="w-3 h-3" style={{ color: C.slateMuted }} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Totals band. IQC = pending inspection = GRN − Accepted − Rejected.
            Accepted + Rejected = fully-inspected qty; sum with IQC = GRN. */}
        <div className="grid grid-cols-4 gap-0 border-b" style={{ borderColor: C.border }}>
          {[
            { k: 'grn',      lbl: 'GRN',           color: C.primaryText,
              val: t.grn ?? 0 },
            { k: 'iqc',      lbl: 'IQC Pending',   color: C.warn,
              val: t.iqc ?? 0 },
            { k: 'accepted', lbl: 'IQC Accepted',  color: C.success,
              val: t.accepted ?? 0 },
            { k: 'rejected', lbl: 'IQC Rejected',  color: C.danger,
              val: t.rejected ?? 0 },
          ].map(m => (
            <div key={m.k} className="p-2 text-center" style={{ borderRight: `1px solid ${C.border}` }}>
              <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>{m.lbl}</div>
              <div className="text-sm font-bold" style={{ color: m.color }}>
                {m.val}<span className="text-[9px] ml-0.5" style={{ color: C.slateMuted }}>{unit}</span>
              </div>
            </div>
          ))}
        </div>
        {t.grn > 0 && (
          <div className="px-3 py-1 text-[9px] text-center" style={{ background: C.surfaceAlt, color: C.slateMuted, borderBottom: `1px solid ${C.border}` }}>
            IQC Accepted + IQC Rejected + IQC Pending = GRN — currently{' '}
            <b style={{ color: t.accepted + t.rejected + t.iqc === t.grn ? C.success : C.danger }}>
              {t.accepted} + {t.rejected} + {t.iqc} = {t.accepted + t.rejected + t.iqc}
            </b>{' '} of <b>{t.grn}</b>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto p-3">
          {busy && !data && (
            <div className="p-6 flex items-center justify-center text-sm gap-2" style={{ color: C.slateMuted }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {receipts.length === 0 && !busy && (
            <div className="p-4 text-center text-xs" style={{ color: C.slateMuted }}>
              No receipts logged yet — click <b>Log New Receipt</b> below.
            </div>
          )}
          {receipts.map((r, i) => {
            const isOpen = inspectFor?.receiptNumber === r.receiptNumber;
            return (
              <div key={r.receiptNumber + '-' + i}
                className="mb-2 rounded-lg border overflow-hidden"
                style={{ borderColor: isOpen ? C.accent : C.border }}
                data-testid={`receipt-${r.receiptNumber}`}>
                <div className="p-2 flex items-center gap-2" style={{ background: C.surfaceAlt }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold" style={{ color: C.primaryText }}>{r.receiptNumber}</div>
                    <div className="text-[9px]" style={{ color: C.slateMuted }}>
                      Logged <b>{(r.receiptDate || '').slice(0, 10)}</b> by <b>{r.createdBy || 'system'}</b>
                      {r.inspectedAt && <>
                        {' · '}Inspected <b>{r.inspectedAt.slice(0, 10)}</b> by <b>{r.inspectedBy || 'system'}</b>
                      </>}
                    </div>
                  </div>
                  <div className="flex gap-2 text-[10px] font-mono">
                    <span title="GRN qty (delivered)"                 style={{ color: C.primaryText }}>GRN {r.grnQty || 0}</span>
                    <span title="Pending IQC inspection"              style={{ color: C.warn }}>Pending {r.iqcQty || 0}</span>
                    <span title="IQC Accepted (passed inspection)"    style={{ color: C.success }}>OK {r.acceptedQty || 0}</span>
                    <span title="IQC Rejected (failed inspection)"    style={{ color: C.danger }}>REJ {r.rejectedQty || 0}</span>
                  </div>
                  <Button size="sm" variant={isOpen ? 'default' : 'outline'}
                    onClick={() => setInspectFor(isOpen ? null : r)}
                    disabled={(r.iqcQty || 0) <= 0 && !isOpen}
                    className="h-6 text-[10px]"
                    data-testid={`inspect-${r.receiptNumber}`}
                    title={(r.iqcQty || 0) <= 0 ? 'All inspected — nothing pending' : 'Split Pending → IQC Accepted / IQC Rejected'}>
                    <ClipboardCheck className="w-3 h-3 mr-1" /> Inspect Pending
                  </Button>
                </div>
                {r.documentRef && (
                  <a href={r.documentRef} target="_blank" rel="noreferrer"
                    className="block px-2 py-1 text-[10px] underline"
                    style={{ color: C.primaryText, borderTop: `1px dashed ${C.border}` }}>
                    <FileText className="w-3 h-3 inline mr-1" /> GRN Doc <ExternalLink className="w-2.5 h-2.5 inline" />
                  </a>
                )}
                {r.inspectionReportRef && (
                  <a href={r.inspectionReportRef} target="_blank" rel="noreferrer"
                    className="block px-2 py-1 text-[10px] underline"
                    style={{ color: C.success, borderTop: `1px dashed ${C.border}` }}>
                    <PackageCheck className="w-3 h-3 inline mr-1" /> Inspection Report <ExternalLink className="w-2.5 h-2.5 inline" />
                  </a>
                )}
                {isOpen && (
                  <div className="p-2 space-y-2" style={{ background: '#FEF3C720', borderTop: `1px solid ${C.border}` }}
                    data-testid={`inspect-form-${r.receiptNumber}`}>
                    <div className="text-[10px] font-semibold" style={{ color: C.slate }}>
                      Split Pending IQC ({r.iqcQty || 0} {unit}) into IQC Accepted / IQC Rejected.
                      Sum must equal or be less than pending qty.
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] uppercase" style={{ color: C.success }}>IQC Accepted</label>
                        <input type="number" min="0" value={acc} onChange={(e) => setAcc(e.target.value)}
                          className="w-full px-2 py-1 rounded border text-[11px]"
                          style={{ borderColor: C.border }} data-testid="inspect-accepted" />
                      </div>
                      <div>
                        <label className="text-[9px] uppercase" style={{ color: C.danger }}>IQC Rejected</label>
                        <input type="number" min="0" value={insRej} onChange={(e) => setInsRej(e.target.value)}
                          className="w-full px-2 py-1 rounded border text-[11px]"
                          style={{ borderColor: C.border }} data-testid="inspect-rejected" />
                      </div>
                    </div>
                    {/* Bin locations — prefilled from MCI default, editable per split */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] uppercase flex items-center gap-1" style={{ color: C.success }}>
                          <MapPin className="w-2.5 h-2.5" /> Accepted → Bin
                        </label>
                        <input value={insAcceptLoc} onChange={(e) => setInsAcceptLoc(e.target.value)}
                          placeholder={pinnedLoc || 'WH-01 · Bay 3'}
                          className="w-full px-2 py-1 rounded border text-[11px]"
                          style={{ borderColor: C.border }} data-testid="inspect-accept-loc" />
                      </div>
                      <div>
                        <label className="text-[9px] uppercase flex items-center gap-1" style={{ color: C.danger }}>
                          <MapPin className="w-2.5 h-2.5" /> Rejected → Quarantine
                        </label>
                        <input value={insRejectLoc} onChange={(e) => setInsRejectLoc(e.target.value)}
                          placeholder="e.g. QUAR-01 · Hold Cage"
                          className="w-full px-2 py-1 rounded border text-[11px]"
                          style={{ borderColor: C.border }} data-testid="inspect-reject-loc" />
                      </div>
                    </div>
                    <input value={insRem} onChange={(e) => setInsRem(e.target.value)}
                      placeholder="Remarks (optional)…"
                      className="w-full px-2 py-1 rounded border text-[11px]"
                      style={{ borderColor: C.border }} />
                    <div>
                      <label className="text-[9px] uppercase" style={{ color: C.slateMuted }}>Inspection Report</label>
                      <DocumentUploader projectId={projectId} currentUrl={insDoc}
                        label="Attach Report" testId="inspect-report-upl"
                        onUploaded={(url) => setInsDoc(url)} />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setInspectFor(null)}>Cancel</Button>
                      <Button size="sm" onClick={submitInspect} disabled={busy}
                        data-testid="inspect-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PackageCheck className="w-3.5 h-3.5 mr-1" />}
                        Save Inspection
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* New receipt form / Trigger */}
        {showNew ? (
          <div className="p-3 border-t space-y-2" style={{ borderColor: C.border, background: C.surfaceAlt }}
            data-testid="new-receipt-form">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-bold" style={{ color: C.primaryText }}>New Receipt</div>
              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                Remaining scope: <b style={{ color: C.danger }}>{t.remaining ?? scope}</b> {unit}
              </div>
            </div>
            <input value={rn} onChange={(e) => setRn(e.target.value)}
              placeholder="Receipt # (e.g. GRN-008-04)"
              className="w-full px-2 py-1.5 rounded border text-[11px] font-mono"
              style={{ borderColor: C.border }} data-testid="rec-number" />

            {/* Primary input: GRN Qty */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[9px] uppercase font-bold" style={{ color: C.primaryText }}>How much was delivered? (GRN Qty)</label>
                {(() => {
                  const remaining = Number(t.remaining ?? Math.max(0, Number(scope || 0) - Number(t.grn ?? 0)));
                  return remaining > 0 ? (
                    <button type="button" onClick={() => setGrn(String(remaining))}
                      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{ background: `${C.accent}18`, color: C.accent, border: `1px solid ${C.accent}40` }}
                      data-testid="rec-grn-fill-remaining"
                      title={`Fill full remaining PO qty (${remaining} ${unit || ''})`}>
                      Fill remaining · {remaining}
                    </button>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <input type="number" min="0" value={grn} onChange={(e) => setGrn(e.target.value)}
                  placeholder="0"
                  className="flex-1 px-2 py-1.5 rounded border text-[13px] font-bold text-right"
                  style={{ borderColor: C.primary, background: 'var(--card-bg)', color: C.primaryText }}
                  data-testid="rec-grn" />
                <span className="text-[10px] font-bold" style={{ color: C.slateMuted }}>{unit}</span>
              </div>
              {/* Live over-scope warning */}
              {(() => {
                const remaining = Number(t.remaining ?? Math.max(0, Number(scope || 0) - Number(t.grn ?? 0)));
                const g = Number(grn || 0);
                if (g > 0 && remaining > 0 && g > remaining) {
                  return (
                    <div className="text-[9.5px] mt-0.5" style={{ color: C.danger }}>
                      ⚠ {g - remaining} {unit || ''} over remaining PO qty — receipt will be flagged as over-shipped.
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Optional split — most receipts default to 100% Pending */}
            <details className="text-[10px]" open={!!(acc || rej)}>
              <summary className="cursor-pointer font-semibold py-1" style={{ color: C.slateMuted }}>
                Optional · Split any qty inspected on arrival
              </summary>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <div>
                  <label className="text-[9px] uppercase" style={{ color: C.success }}>Accepted on Arrival</label>
                  <input type="number" min="0" value={acc} onChange={(e) => setAcc(e.target.value)}
                    placeholder="0"
                    className="w-full px-2 py-1 rounded border text-[11px] text-right"
                    style={{ borderColor: C.border }} data-testid="rec-acc" />
                </div>
                <div>
                  <label className="text-[9px] uppercase" style={{ color: C.danger }}>Rejected on Arrival</label>
                  <input type="number" min="0" value={rej} onChange={(e) => setRej(e.target.value)}
                    placeholder="0"
                    className="w-full px-2 py-1 rounded border text-[11px] text-right"
                    style={{ borderColor: C.border }} data-testid="rec-rej" />
                </div>
              </div>
            </details>

            {/* Live split preview */}
            {num(grn) > 0 && (() => {
              const g = num(grn), a = num(acc), r = num(rej);
              const pending = Math.max(0, g - a - r);
              const over = a + r > g;
              return (
                <div className="p-2 rounded text-[10px] font-mono"
                  style={{ background: over ? 'var(--tone-red-bg)' : 'var(--tone-blue-bg)',
                            border: `1px solid ${over ? C.danger : C.primary}20`,
                            color: C.slate }}
                  data-testid="rec-split-preview">
                  <b>{g}</b> GRN ={' '}
                  <b style={{ color: C.warn }}>{pending}</b> Pending +{' '}
                  <b style={{ color: C.success }}>{a}</b> Accepted +{' '}
                  <b style={{ color: C.danger }}>{r}</b> Rejected
                  {over && <div className="mt-0.5" style={{ color: C.danger }}>⚠ Accepted + Rejected exceeds GRN</div>}
                </div>
              );
            })()}

            <input value={rem} onChange={(e) => setRem(e.target.value)}
              placeholder="Remarks (optional)"
              className="w-full px-2 py-1 rounded border text-[11px]"
              style={{ borderColor: C.border }} />

            {/* Storage location — cascading Warehouse (BIN) → Rack dropdowns.
                The chosen rack code lands in receipt.acceptedLocation which
                Consolidate-to-Inventory reads to flow material into the right rack. */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9px] uppercase" style={{ color: C.primaryText }}>Warehouse (Bin)</label>
                <select value={selectedBin}
                  onChange={(e) => { setSelectedBin(e.target.value); setSelectedRack(''); }}
                  className="w-full px-2 py-1 rounded border text-[11px]"
                  style={{ borderColor: C.border, background: 'var(--card-bg)' }}
                  data-testid="rec-warehouse-bin">
                  <option value="">— select bin —</option>
                  {(hierarchy?.bins || []).map(b => (
                    <option key={b.id} value={b.location}>{b.label || b.location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] uppercase" style={{ color: C.primaryText }}>Rack</label>
                <select value={selectedRack}
                  onChange={(e) => setSelectedRack(e.target.value)}
                  disabled={!selectedBin}
                  className="w-full px-2 py-1 rounded border text-[11px]"
                  style={{ borderColor: C.border, background: selectedBin ? '#fff' : 'var(--tone-slate-bg)' }}
                  data-testid="rec-warehouse-rack">
                  <option value="">— select rack —</option>
                  {(hierarchy?.bins || []).find(b => b.location === selectedBin)?.racks.map(r => (
                    <option key={r.id} value={r.location}>{r.label || r.location}</option>
                  ))}
                </select>
              </div>
            </div>
            {selectedRack && (
              <div className="text-[9px] font-mono px-2 py-1 rounded" style={{ background: 'var(--card-bg-alt)', color: C.primaryText }}>
                Stock will land at: <b>{selectedRack}</b>
              </div>
            )}
            <div>
              <label className="text-[9px] uppercase" style={{ color: C.primaryText }}>Shop / Yard (optional)</label>
              <input value={shop} onChange={(e) => setShop(e.target.value)}
                placeholder="e.g. Fabrication Shop 2"
                className="w-full px-2 py-1 rounded border text-[11px]"
                style={{ borderColor: C.border }} data-testid="rec-shop" />
            </div>
            <div>
              <label className="text-[9px] uppercase" style={{ color: C.slateMuted }}>GRN Document</label>
              <DocumentUploader projectId={projectId} currentUrl={docUrl}
                label="Attach GRN Doc" testId="grn-doc-upl"
                onUploaded={(url) => setDocUrl(url)} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button size="sm" onClick={submitNew} disabled={busy || !num(grn)}
                data-testid="rec-submit">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PackagePlus className="w-3.5 h-3.5 mr-1" />}
                Save Receipt
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-3 border-t flex justify-between items-center" style={{ borderColor: C.border, background: C.surfaceAlt }}>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              {receipts.length} receipt{receipts.length === 1 ? '' : 's'} on this MCI
            </div>
            <Button size="sm" onClick={() => setShowNew(true)} data-testid="log-new-receipt-btn"
              className="text-white" style={{ background: C.accent }}>
              <PackagePlus className="w-3.5 h-3.5 mr-1" /> Log New Receipt
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
